import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { m, AnimatePresence, LazyMotion, domAnimation } from 'motion/react';
import {
  Sparkles,
  Trophy,
  Zap,
  Rocket,
  Globe,
  ChevronDown,
  ArrowRight,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@ponzinomics/ui';
import AuroraBlur from '@/components/react-bits/aurora-blur';
import PerspectiveGrid from '@/components/react-bits/perspective-grid';
import DotShift from '@/components/react-bits/dot-shift';
import { presaleApi, type Presale } from '@/lib/presale-api';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Feature {
  icon: LucideIcon;
  label: string;
  href: string;
}
interface Section {
  id: string;
  name: string;
  tagline: string;
  accent: string;
  bg: 'aurora' | 'grid' | 'dots';
  features: Feature[];
}

// ─── Config ───────────────────────────────────────────────────────────────────

const SECTIONS: Section[] = [
  {
    id: 'ponzinomics',
    name: 'Ponzinomics',
    tagline: 'Memes · Quests · Leaderboards',
    accent: '#c4f70e',
    bg: 'aurora',
    features: [
      { icon: Sparkles, label: 'Meme Generator', href: '/ponzinomics/meme' },
      { icon: Trophy, label: 'Quests', href: '/ponzinomics/quests' },
      { icon: Zap, label: 'Leaderboards', href: '/ponzinomics/leaderboards' },
    ],
  },
  {
    id: 'presale',
    name: 'Presale',
    tagline: 'Block-0 launches · Creator fee vault · Auto-rewards',
    accent: '#a78bfa',
    bg: 'grid',
    features: [
      { icon: Rocket, label: 'Discover', href: '/presale' },
      { icon: Zap, label: 'Create Launch', href: '/presale/create' },
      { icon: Trophy, label: 'My Presales', href: '/presale/my-presales' },
    ],
  },
  {
    id: 'builder',
    name: 'Website Builder',
    tagline: 'Token sites · Drag & drop · Instant publish',
    accent: '#38bdf8',
    bg: 'dots',
    features: [{ icon: Globe, label: 'Open Builder', href: '/builder' }],
  },
];

// ─── Section background ───────────────────────────────────────────────────────

function SectionBg({ type, accent }: { type: Section['bg']; accent: string }): React.ReactElement {
  if (type === 'aurora')
    return (
      <div className="absolute inset-0" style={{ opacity: 0.5 }}>
        <AuroraBlur
          layers={[
            { color: accent, speed: 0.4, intensity: 0.2 },
            { color: '#0a0a14', speed: 0.2, intensity: 0.4 },
          ]}
          skyLayers={[{ color: '#0a0a14', blend: 1 }]}
          width="100%"
          height="100%"
        />
      </div>
    );
  if (type === 'grid')
    return (
      <div className="absolute inset-0" style={{ opacity: 0.12 }}>
        <PerspectiveGrid color={accent} speed={0.4} gridScale={1} width="100%" height="100%" />
      </div>
    );
  return (
    <div className="absolute inset-0" style={{ opacity: 0.1 }}>
      <DotShift color={accent} speed={0.6} scale={0.6} />
    </div>
  );
}

// ─── Previews ─────────────────────────────────────────────────────────────────

function PonzinomicsPreview(): React.ReactElement {
  const navigate = useNavigate();
  const CARDS = [
    {
      icon: Sparkles,
      title: 'AI Meme Generator',
      desc: 'Brand-consistent memes in 3s',
      accent: '#c4f70e',
      href: '/ponzinomics/meme',
    },
    {
      icon: Trophy,
      title: 'Quest System',
      desc: 'Twitter, Discord, on-chain tasks',
      accent: '#f59e0b',
      href: '/ponzinomics/quests',
    },
    {
      icon: Zap,
      title: 'Leaderboards',
      desc: 'Real-time ranking, any metric',
      accent: '#818cf8',
      href: '/ponzinomics/leaderboards',
    },
  ];
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {CARDS.map(({ icon: Icon, title, desc, accent, href }) => (
        <button
          key={title}
          type="button"
          onClick={() => navigate(href)}
          className="group relative overflow-hidden rounded-xl border border-white/[0.06] bg-white/[0.03] p-5 text-left transition-all hover:border-white/[0.12] hover:bg-white/[0.05]"
        >
          <div
            className="mb-3 flex h-9 w-9 items-center justify-center rounded-lg"
            style={{ background: `${accent}18` }}
          >
            <Icon className="h-4 w-4" style={{ color: accent }} />
          </div>
          <p className="font-semibold text-white">{title}</p>
          <p className="mt-0.5 text-sm text-gray-500">{desc}</p>
          <ArrowRight className="absolute bottom-4 right-4 h-4 w-4 text-gray-600 opacity-0 transition-all group-hover:translate-x-0.5 group-hover:opacity-100" />
        </button>
      ))}
    </div>
  );
}

function PresalePreview(): React.ReactElement {
  const navigate = useNavigate();
  const [presales, setPresales] = useState<Presale[]>([]);
  useEffect(() => {
    presaleApi
      .discover()
      .then(setPresales)
      .catch(() => {});
  }, []);

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {presales.slice(0, 3).map((p) => {
        const progress = Math.min((Number(p.totalRaised) / p.targetSol) * 100, 100);
        return (
          <button
            key={p.id}
            type="button"
            onClick={() => navigate(`/presale/${p.id}`)}
            className="notch-border notch-border-white/8 notch-bg-white/3 group relative overflow-hidden p-5 text-left transition-all hover:[--notch-bg:rgba(255,255,255,0.05)] hover:[--notch-border-color:rgba(167,139,250,0.3)]"
          >
            <div className="mb-3 flex items-center gap-3">
              {p.imageUrl && (
                <img
                  src={p.imageUrl}
                  alt={p.tokenName}
                  className="h-9 w-9 rounded-full object-cover"
                />
              )}
              <div className="min-w-0 flex-1">
                <p className="truncate font-semibold text-white">{p.tokenName}</p>
                <p className="font-mono text-xs text-gray-500">${p.tokenSymbol}</p>
              </div>
              <span
                className={cn(
                  'shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase',
                  p.status === 'OPEN'
                    ? 'bg-[#c4f70e]/10 text-[#c4f70e]'
                    : 'bg-[#a78bfa]/10 text-[#a78bfa]'
                )}
              >
                {p.status}
              </span>
            </div>
            <div className="mb-2 h-1 overflow-hidden rounded-full bg-white/10">
              <div
                className="h-full rounded-full bg-[#a78bfa] transition-all"
                style={{ width: `${progress}%` }}
              />
            </div>
            <div className="flex justify-between text-xs text-gray-500">
              <span>
                {Number(p.totalRaised).toFixed(1)} / {p.targetSol} SOL
              </span>
              <span>{p.maxBuyers - p.buyerCount} spots left</span>
            </div>
          </button>
        );
      })}
      <button
        type="button"
        onClick={() => navigate('/presale')}
        className="notch-border notch-border-white/8 notch-bg-white/3 flex items-center justify-center gap-2 p-5 text-sm text-gray-500 transition-colors hover:text-[#a78bfa] hover:[--notch-border-color:rgba(167,139,250,0.3)]"
      >
        <ArrowRight className="h-4 w-4" /> View all presales
      </button>
    </div>
  );
}

function BuilderPreview(): React.ReactElement {
  const navigate = useNavigate();
  return (
    <div className="flex flex-col items-center justify-center gap-4 rounded-xl border border-dashed border-white/[0.07] py-12 text-center">
      <Globe className="h-10 w-10 text-[#38bdf8]/50" />
      <div>
        <p className="font-semibold text-white">Launch your token site</p>
        <p className="mt-1 text-sm text-gray-500">Deploy from contract metadata in one click</p>
      </div>
      <button
        type="button"
        onClick={() => navigate('/builder')}
        className="clip-notched bg-[#38bdf8]/10 px-4 py-2 text-sm font-medium text-[#38bdf8] transition-colors hover:bg-[#38bdf8]/20"
      >
        Open Builder <ArrowRight className="h-4 w-4" />
      </button>
    </div>
  );
}

const PREVIEW: Record<string, React.ReactElement> = {
  ponzinomics: <PonzinomicsPreview />,
  presale: <PresalePreview />,
  builder: <BuilderPreview />,
};

// ─── Expandable section ───────────────────────────────────────────────────────

function ProductSection({ section }: { section: Section }): React.ReactElement {
  const [open, setOpen] = useState(section.id === 'ponzinomics');
  const navigate = useNavigate();

  return (
    <div className="overflow-hidden rounded-2xl border border-white/[0.06] bg-gray-950/60 backdrop-blur-sm">
      {/* Header */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="group relative flex w-full items-center gap-4 overflow-hidden px-6 py-5 text-left"
      >
        <div className="pointer-events-none absolute inset-0">
          <SectionBg type={section.bg} accent={section.accent} />
        </div>
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-gray-950/95 via-gray-950/75 to-gray-950/40" />

        <div className="relative flex min-w-0 flex-1 items-center gap-4">
          <div
            className="h-2 w-2 shrink-0 rounded-full"
            style={{ background: section.accent, boxShadow: `0 0 10px ${section.accent}` }}
          />
          <div className="min-w-0 flex-1">
            <span className="font-bold text-white" style={{ fontSize: '1.05rem' }}>
              {section.name}
            </span>
            <span className="ml-3 text-sm text-gray-500">{section.tagline}</span>
          </div>
          {/* Quick pills — desktop only */}
          <div className="hidden items-center gap-2 lg:flex">
            {section.features.slice(0, 3).map(({ icon: Icon, label, href }) => (
              <button
                key={href}
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(href);
                }}
                className="flex items-center gap-1.5 rounded-full border border-white/[0.06] bg-white/[0.04] px-3 py-1 text-xs text-gray-400 transition-colors hover:border-white/[0.14] hover:text-white"
              >
                <Icon className="h-3 w-3" />
                {label}
              </button>
            ))}
          </div>
        </div>

        <m.div
          className="relative shrink-0"
          animate={{ rotate: open ? 180 : 0 }}
          transition={{ duration: 0.2 }}
        >
          <ChevronDown className="h-5 w-5 text-gray-500" />
        </m.div>
      </button>

      {/* Body */}
      <AnimatePresence initial={false}>
        {open && (
          <m.div
            key="body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
            className="overflow-hidden"
          >
            <div className="border-t border-white/[0.04] px-6 py-6">{PREVIEW[section.id]}</div>
          </m.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function HomePage(): React.ReactElement {
  return (
    <LazyMotion features={domAnimation}>
      <div className="space-y-3">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-white">Welcome back</h1>
          <p className="mt-1 text-sm text-gray-500">Your Ponzinomics command centre</p>
        </div>
        {SECTIONS.map((s) => (
          <ProductSection key={s.id} section={s} />
        ))}
      </div>
    </LazyMotion>
  );
}
